import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Button,
  Chip,
  Dialog,
  FormControl,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  ThemeProvider,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import ImageNotSupportedIcon from "@mui/icons-material/ImageNotSupported";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import SearchIcon from "@mui/icons-material/Search";
import FilterAltOffOutlinedIcon from "@mui/icons-material/FilterAltOffOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { selectUser } from "../../features/userSlice";
import { selectRoles } from "../../features/roleSlice";
import { sendUserNotification } from "../../utilities";
import {
  fetchOrderProblems,
  fetchOrderProblem,
  fetchOrderInfo,
  updateOrderProblemStatus,
  addOrderProblemNote,
  reporterLabel,
} from "./orderProblemsApi";
import OrderProblemsAnalytics, { Segmented } from "./OrderProblemsAnalytics";
import { useAppMuiTheme } from "../../theme/MuiThemeBridge";
import { PageHeader, PageShell } from "../../components/UI/Page";
import { isPathGranted } from "../../utils/routeAccess";
import {
  EmptyState,
  LoadingState,
  Spinner,
  StatTile,
} from "../../components/UI/States";

export const ORDER_PROBLEM_DASHBOARD_ROLES = ["Admin", "Executive Team"];

const STATUS_META = {
  open: { label: "Open", chip: "error" },
  resolved: { label: "Resolved", chip: "success" },
};
const STATUS_ORDER = ["open", "resolved"];

// Driver app problem types (same enum as the mobile server) — prettify
// anything we don't have an explicit label for.
const PROBLEM_TYPE_LABELS = {
  damaged_material: "Damaged Material",
  wrong_or_missing_material: "Wrong / Missing Material",
  jobsite_access: "Jobsite Access",
  customer_issue: "Customer Issue",
  truck_equipment: "Truck / Equipment",
  other: "Other",
};

const problemTypeLabel = (type) => {
  if (!type) return "Unspecified";
  if (PROBLEM_TYPE_LABELS[type]) return PROBLEM_TYPE_LABELS[type];
  return String(type)
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const statusLabel = (status) =>
  STATUS_META[status]?.label || problemTypeLabel(status);

const timeAgo = (value) => {
  if (!value) return "";
  const seconds = Math.floor((Date.now() - new Date(value).getTime()) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(value).toLocaleDateString();
};

const StatusChip = ({ status, size = "small" }) => (
  <Chip
    label={statusLabel(status)}
    color={STATUS_META[status]?.chip || "default"}
    size={size}
  />
);

// Driver-app images store a full data URL in `data`; handle raw base64 too.
const imageSrc = (image) => {
  if (!image?.data) return null;
  return image.data.startsWith("data:")
    ? image.data
    : `data:${image.mimeType || "image/jpeg"};base64,${image.data}`;
};

// Web-form submissions carry a list of problem types in `errors`; driver-app
// submissions carry a single problemType.
const ProblemChips = ({ problem }) =>
  problem.errors?.length ? (
    <div className="flex flex-wrap gap-1">
      {problem.errors.map((e) => (
        <Chip key={e} label={e} size="small" variant="outlined" />
      ))}
    </div>
  ) : (
    <Chip
      label={problemTypeLabel(problem.problemType)}
      size="small"
      variant="outlined"
    />
  );

// Live Webtrack status for the problem's order. null = still loading.
const OrderStatusChip = ({ info }) => {
  if (!info) return <span className="app-muted text-xs">Checking...</span>;
  if (!info.found)
    return <span className="app-muted text-xs">Not found</span>;
  return (
    <Chip
      label={info.status || "Unknown"}
      size="small"
      variant="outlined"
      color={info.invoiced ? "success" : "info"}
    />
  );
};

// ---- Photo lightbox ----

const PhotoLightbox = ({ image, onClose }) => {
  const src = imageSrc(image);
  return (
    <Dialog open={!!image} onClose={onClose} maxWidth="lg">
      {image && (
        <div className="flex flex-col">
          <div className="flex items-center justify-between gap-4 border-b app-divider px-4 py-2.5">
            <p className="min-w-0 truncate text-sm font-medium">
              {image.name || "Photo"}
            </p>
            <div className="flex shrink-0 items-center gap-1">
              {src && (
                <Button
                  size="small"
                  startIcon={<DownloadIcon />}
                  component="a"
                  href={src}
                  download={image.name || "order-problem-photo.jpg"}
                >
                  Download
                </Button>
              )}
              <Button size="small" startIcon={<CloseIcon />} onClick={onClose}>
                Close
              </Button>
            </div>
          </div>
          <div className="flex items-center justify-center bg-slate-950/90 p-2">
            {src ? (
              <img
                src={src}
                alt={image.name || "Order problem photo"}
                className="max-h-[75vh] max-w-full rounded-lg object-contain"
              />
            ) : (
              <p className="app-muted p-10 text-sm">
                This photo has no image data.
              </p>
            )}
          </div>
        </div>
      )}
    </Dialog>
  );
};

// ---- Detail view ----

// Notes and status changes live in separate arrays on the document (notes
// are shown to drivers in the mobile app; statusHistory is internal). Legacy
// docs may still have status entries mixed into notes — normalize everything
// into one timeline.
const buildActivity = (problem) => {
  const entries = [];
  (problem?.notes || []).forEach((n, index) => {
    const time = n.timestamp || n.createdAt || null;
    if (n.type === "status") {
      entries.push({
        key: String(n._id || n.id || `note-${index}`),
        kind: "status",
        from: n.from,
        to: n.to,
        by: n.author?.name || n.authorName || "",
        time,
      });
    } else {
      entries.push({
        key: String(n._id || n.id || `note-${index}`),
        kind: "note",
        text: n.text || "",
        by: n.authorName || n.author?.name || "",
        time,
      });
    }
  });
  (problem?.statusHistory || []).forEach((s, index) => {
    entries.push({
      key: String(s._id || `status-${index}`),
      kind: "status",
      from: s.from,
      to: s.to,
      by: s.changedByName || "",
      time: s.timestamp || null,
    });
  });
  return entries.sort((a, b) => new Date(a.time || 0) - new Date(b.time || 0));
};

const ActivityEntry = ({ entry }) => {
  if (entry.kind === "status") {
    return (
      <div className="rounded-xl border border-sky-500/30 bg-sky-500/10 p-3 text-xs">
        <p className="font-semibold text-sky-800 dark:text-sky-200">
          ◉ {entry.by || "Someone"} moved this from {statusLabel(entry.from)}{" "}
          to {statusLabel(entry.to)}
        </p>
        <p className="app-muted mt-1 text-[11px]">
          {entry.time ? new Date(entry.time).toLocaleString() : ""}
        </p>
      </div>
    );
  }
  return (
    <div className="app-surface rounded-xl border p-3">
      <div className="flex items-baseline justify-between gap-2">
        <p className="app-accent text-xs font-semibold">
          {entry.by || "Unknown"}
        </p>
        <p className="app-muted shrink-0 text-[11px]">
          {entry.time ? new Date(entry.time).toLocaleString() : ""}
        </p>
      </div>
      <p className="mt-1 whitespace-pre-wrap break-words text-sm">
        {entry.text}
      </p>
    </div>
  );
};

const DetailField = ({ label, children }) => (
  <div>
    <p className="app-eyebrow">{label}</p>
    <div className="mt-1 text-sm font-medium">{children || "—"}</div>
  </div>
);

const ProblemDetail = ({ problemId, user, onBack, onChanged }) => {
  const [problem, setProblem] = useState(null);
  const [error, setError] = useState(null);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [noteDraft, setNoteDraft] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);
  const [orderInfo, setOrderInfo] = useState(null);

  useEffect(() => {
    setOrderInfo(null);
    if (problem?.orderNumber) {
      fetchOrderInfo(problem.orderNumber)
        .then(setOrderInfo)
        .catch(() => setOrderInfo({ found: false }));
    }
  }, [problem?.orderNumber]);

  const load = () => {
    fetchOrderProblem(problemId)
      .then((doc) => {
        setProblem(doc);
        setError(null);
      })
      .catch((err) => setError(err.message));
  };

  useEffect(() => {
    setProblem(null);
    load();
  }, [problemId]);

  const author = { name: user?.name || "", email: user?.email || "" };

  // In-app notification to whoever reported the problem. No redirect: the
  // dashboard is Admin/Executive-only, so reporters have no page to open.
  // Anonymous web submissions have nobody to notify — only driver-app reports
  // (and web reports filed before the anonymous switch) carry a reporter.
  const notifyReporter = (subject, message) => {
    if (problem?.anonymous) return;
    const email = problem?.reporterEmail;
    if (!email || email === user?.email?.toLowerCase()) return;
    sendUserNotification(email, user?.name || "Big C Node", message, subject);
  };

  const handleStatusChange = async (status) => {
    if (!problem || savingStatus || status === problem.status) return;
    setSavingStatus(true);
    try {
      await updateOrderProblemStatus(problemId, status, author);
      notifyReporter(
        "Order Problem update",
        `Your Order Problem for order ${problem.orderNumber || "—"} was marked ${status}.`
      );
      load();
      onChanged?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingStatus(false);
    }
  };

  const handleAddNote = async () => {
    const text = noteDraft.trim();
    if (!text || savingNote) return;
    setSavingNote(true);
    try {
      await addOrderProblemNote(problemId, text, author);
      notifyReporter(
        "Note on your Order Problem",
        `${user?.name || "Big C Node"} added a note on order ${
          problem?.orderNumber || "—"
        }: "${text}"`
      );
      setNoteDraft("");
      load();
      onChanged?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingNote(false);
    }
  };

  const activity = buildActivity(problem);
  const images = problem?.images || [];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="-ml-2 flex items-center gap-2">
          <Button startIcon={<ArrowBackIcon />} onClick={onBack}>
            All Problems
          </Button>
        </div>
        {problem && (
          <div className="flex items-center gap-2">
            <span className="app-muted text-sm" id="problem-status-label">
              Status
            </span>
            <FormControl size="small" className="min-w-[9rem]">
              <Select
                value={problem.status || "open"}
                disabled={savingStatus}
                onChange={(e) => handleStatusChange(e.target.value)}
                labelId="problem-status-label"
              >
                {STATUS_ORDER.map((s) => (
                  <MenuItem key={s} value={s}>
                    {statusLabel(s)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        )}
      </div>

      {error && (
        <div
          role="alert"
          className="rounded-xl bg-rose-500/10 px-4 py-3 text-sm font-medium text-rose-700 dark:text-rose-300"
        >
          {error}
        </div>
      )}

      {!problem && !error && <LoadingState label="Loading order problem…" />}

      {problem && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* Left: problem details */}
          <div className="min-w-0 space-y-6 lg:col-span-2">
            <div className="app-card rounded-2xl border p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <h2 className="text-xl font-bold tabular-nums tracking-tight sm:text-2xl">
                    Order #{problem.orderNumber || "—"}
                  </h2>
                  <p className="app-muted mt-0.5 text-sm">
                    {problem.customer || "Unknown customer"}
                  </p>
                </div>
                <div className="flex flex-wrap items-center justify-end gap-1.5">
                  <ProblemChips problem={problem} />
                  <StatusChip status={problem.status} />
                </div>
              </div>

              <div className="mt-5 grid grid-cols-2 gap-x-4 gap-y-5 border-t app-divider pt-5 sm:grid-cols-3">
                <DetailField label="Reported By">
                  <p>{reporterLabel(problem)}</p>
                  {problem.reporterEmail && (
                    <a
                      href={`mailto:${problem.reporterEmail}`}
                      className="app-link break-all text-xs font-normal"
                    >
                      {problem.reporterEmail}
                    </a>
                  )}
                </DetailField>
                <DetailField label="Branch">{problem.branch}</DetailField>
                {problem.orderNumber && (
                  <DetailField label="Order Status">
                    <OrderStatusChip info={orderInfo} />
                  </DetailField>
                )}
                {orderInfo?.found && orderInfo.salesRep && (
                  <DetailField label="Sales Rep">
                    <p>{orderInfo.salesRep}</p>
                    {orderInfo.salesRepEmail && (
                      <a
                        href={`mailto:${orderInfo.salesRepEmail}`}
                        className="app-link break-all text-xs font-normal"
                      >
                        {orderInfo.salesRepEmail}
                      </a>
                    )}
                  </DetailField>
                )}
                <DetailField label="Reported">
                  {problem.createdAt && (
                    <>
                      <p>{new Date(problem.createdAt).toLocaleString()}</p>
                      <p className="app-muted text-xs font-normal">
                        {timeAgo(problem.createdAt)}
                      </p>
                    </>
                  )}
                </DetailField>
              </div>
            </div>

            <div className="app-card rounded-2xl border p-5">
              <h3 className="font-semibold">Description</h3>
              <p className="mt-2 whitespace-pre-wrap break-words text-sm leading-relaxed">
                {problem.description || (
                  <span className="app-muted">No description provided.</span>
                )}
              </p>
            </div>

            <div className="app-card rounded-2xl border p-5">
              <h3 className="font-semibold">
                Photos
                {images.length > 0 && (
                  <span className="app-muted ml-1 font-normal tabular-nums">
                    ({images.length})
                  </span>
                )}
              </h3>
              {images.length === 0 ? (
                <p className="app-muted mt-2 text-sm">
                  No photos were attached.
                </p>
              ) : (
                <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
                  {images.map((image, index) => {
                    const src = imageSrc(image);
                    return (
                      <button
                        type="button"
                        key={index}
                        onClick={() => src && setLightboxImage(image)}
                        aria-label={`Open ${image.name || `photo ${index + 1}`}`}
                        className={`group relative aspect-square overflow-hidden rounded-xl border app-divider ${
                          src ? "cursor-zoom-in" : "cursor-default"
                        }`}
                      >
                        {src ? (
                          <img
                            src={src}
                            alt={image.name || `Photo ${index + 1}`}
                            className="h-full w-full object-cover transition-transform duration-150 group-hover:scale-105"
                          />
                        ) : (
                          <div className="flex h-full w-full flex-col items-center justify-center gap-1 bg-slate-500/10">
                            <ImageNotSupportedIcon
                              className="app-muted"
                              fontSize="small"
                            />
                            <span className="app-muted px-2 text-center text-[11px]">
                              {image.name || "Unavailable"}
                            </span>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Right: activity timeline */}
          <div className="app-card flex max-h-[75vh] min-h-[24rem] min-w-0 flex-col overflow-hidden rounded-2xl border lg:sticky lg:top-24">
            <div className="flex items-center justify-between border-b app-divider px-4 py-3">
              <h3 className="font-semibold">Activity</h3>
              {activity.length > 0 && (
                <span className="app-chip app-muted tabular-nums">
                  {activity.length}
                </span>
              )}
            </div>
            <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
              {activity.length === 0 && (
                <p className="app-muted pt-8 text-center text-sm">
                  No activity yet — add the first note.
                </p>
              )}
              {activity.map((entry) => (
                <ActivityEntry key={entry.key} entry={entry} />
              ))}
            </div>
            <div className="flex items-end gap-2 border-t app-divider p-3">
              <TextField
                fullWidth
                size="small"
                multiline
                maxRows={4}
                inputProps={{ "aria-label": "Add a note" }}
                placeholder="Add a note..."
                value={noteDraft}
                onChange={(e) => setNoteDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleAddNote();
                  }
                }}
              />
              <Button
                variant="contained"
                onClick={handleAddNote}
                disabled={!noteDraft.trim() || savingNote}
              >
                Post
              </Button>
            </div>
          </div>
        </div>
      )}

      <PhotoLightbox
        image={lightboxImage}
        onClose={() => setLightboxImage(null)}
      />
    </div>
  );
};

// ---- Stat tiles ----

const STAT_TILES = [
  { key: "open", label: "Open", icon: ErrorOutlineIcon, tone: "rose" },
  {
    key: "resolved",
    label: "Resolved",
    icon: TaskAltIcon,
    tone: "emerald",
  },
  {
    key: "All",
    label: "Total Problems",
    icon: LocalShippingIcon,
    tone: "default",
  },
];

const VIEW_TABS = [
  { key: "problems", label: "Problems" },
  { key: "analytics", label: "Analytics" },
];

// ---- Dashboard ----

const OrderProblemsDashboard = () => {
  const user = useSelector(selectUser);
  const role = useSelector(selectRoles);
  // Roles, or a Page Access grant on this page.
  const canView =
    ORDER_PROBLEM_DASHBOARD_ROLES.some((r) => role?.roles?.includes(r)) ||
    isPathGranted("/order-problems", role?.grantedPaths);

  // The filter Selects, the search field, the note box, the status/problem
  // Chips, and the photo lightbox Dialog are all MUI, which keeps its own
  // palette — on the light default their text renders near-black on the dark
  // cards. Feed the in-app toggle into a MUI theme instead.
  const muiTheme = useAppMuiTheme();

  // Deep-link straight to a problem via ?problem=<id>, or to the analytics
  // tab via ?view=analytics.
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab =
    searchParams.get("view") === "analytics" ? "analytics" : "problems";

  const [problems, setProblems] = useState(null);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState("All");
  const [branchFilter, setBranchFilter] = useState("All");
  const [typeFilter, setTypeFilter] = useState("All");
  const [search, setSearch] = useState("");
  // Derived from the URL (not state) so the browser Back/Forward buttons
  // change the view too.
  const selectedId = searchParams.get("problem") || null;
  const [orderInfo, setOrderInfo] = useState({}); // orderNumber -> lookup result
  const requestedOrdersRef = useRef(new Set());

  const load = () => {
    fetchOrderProblems()
      .then((list) => {
        setProblems(list);
        setError(null);
      })
      .catch((err) => setError(err.message));
  };

  useEffect(() => {
    if (canView) load();
  }, [canView]);

  // Look up the live Webtrack status once per unique order number.
  useEffect(() => {
    [...new Set((problems || []).map((i) => i.orderNumber).filter(Boolean))]
      .filter((n) => !requestedOrdersRef.current.has(n))
      .forEach((n) => {
        requestedOrdersRef.current.add(n);
        fetchOrderInfo(n)
          .then((info) => setOrderInfo((prev) => ({ ...prev, [n]: info })))
          .catch(() =>
            setOrderInfo((prev) => ({ ...prev, [n]: { found: false } }))
          );
      });
  }, [problems]);

  // Keep the active tab in the URL when drilling into a problem, so Back from
  // a problem opened off the analytics tab returns to analytics. Opening a
  // problem PUSHES a history entry so the browser Back button returns to the
  // list/analytics view; the in-page back replaces, to avoid stacking
  // duplicate list entries.
  const selectProblem = (id) => {
    const params = {};
    if (activeTab === "analytics") params.view = "analytics";
    if (id) params.problem = id;
    setSearchParams(params, id ? undefined : { replace: true });
  };

  const selectTab = (tab) => {
    setSearchParams(tab === "analytics" ? { view: "analytics" } : {});
  };

  const branches = useMemo(
    () =>
      [...new Set((problems || []).map((i) => i.branch).filter(Boolean))].sort(),
    [problems]
  );
  const problemTypes = useMemo(
    () =>
      [
        ...new Set((problems || []).map((i) => i.problemType).filter(Boolean)),
      ].sort(),
    [problems]
  );

  const counts = useMemo(() => {
    const c = { All: (problems || []).length, open: 0, resolved: 0 };
    (problems || []).forEach((i) => {
      if (c[i.status] !== undefined) c[i.status] += 1;
    });
    return c;
  }, [problems]);

  const filtered = (problems || []).filter((i) => {
    if (statusFilter !== "All" && i.status !== statusFilter) return false;
    if (branchFilter !== "All" && i.branch !== branchFilter) return false;
    if (typeFilter !== "All" && i.problemType !== typeFilter) return false;
    if (search.trim()) {
      const haystack = [
        i.orderNumber,
        i.customer,
        i.reporterName,
        i.reporterEmail,
        i.branch,
        i.description,
        ...(i.errors || []),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      if (!haystack.includes(search.trim().toLowerCase())) return false;
    }
    return true;
  });

  if (!canView) {
    return (
      <PageShell size="md">
        <div className="app-card rounded-2xl border">
          <EmptyState
            icon={LockOutlinedIcon}
            title="Order Problems"
            description="You need an Admin or Executive Team role to access this dashboard."
          />
        </div>
      </PageShell>
    );
  }

  return (
    <ThemeProvider theme={muiTheme}>
      <PageShell size="2xl">
        <PageHeader
          icon={ReportProblemOutlinedIcon}
          tone="rose"
          eyebrow="Everyday Tools"
          title="Order Problems"
          description="Order problems reported by drivers and staff — review, discuss, and resolve them here."
          actions={
            <Segmented
              label="Dashboard view"
              options={VIEW_TABS}
              value={activeTab}
              onChange={selectTab}
            />
          }
        />

        {selectedId ? (
          <ProblemDetail
            problemId={selectedId}
            user={user}
            onBack={() => selectProblem(null)}
            onChanged={load}
          />
        ) : (
          <div className="space-y-6">
            {error && (
              <div
                role="alert"
                className="rounded-xl bg-rose-500/10 px-4 py-3 text-sm font-medium text-rose-700 dark:text-rose-300"
              >
                {error}
              </div>
            )}

            {activeTab === "analytics" ? (
              problems === null ? (
                <LoadingState label="Loading analytics…" />
              ) : (
                <OrderProblemsAnalytics
                  problems={problems}
                  problemTypeLabel={problemTypeLabel}
                  onOpenProblem={selectProblem}
                />
              )
            ) : (
              <>
                {/* Stat tiles — each one filters the table by status. */}
                <div className="grid grid-cols-3 gap-3 sm:gap-4">
                  {STAT_TILES.map((tile) => (
                    <StatTile
                      key={tile.key}
                      label={tile.label}
                      value={problems === null ? "—" : counts[tile.key]}
                      icon={tile.icon}
                      tone={tile.tone}
                      active={statusFilter === tile.key}
                      onClick={() => setStatusFilter(tile.key)}
                    />
                  ))}
                </div>

                {/* Table with its filter toolbar */}
                <div className="app-card overflow-hidden rounded-2xl border">
                  <div className="flex flex-col gap-3 border-b app-divider p-3 sm:px-4 lg:flex-row lg:items-center lg:justify-between">
                    <Segmented
                      label="Status filter"
                      options={["All", ...STATUS_ORDER].map((s) => ({
                        key: s,
                        label: s === "All" ? "All" : statusLabel(s),
                      }))}
                      value={statusFilter}
                      onChange={setStatusFilter}
                      className="self-start"
                    />
                    <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center">
                      <FormControl size="small" className="w-full sm:w-40">
                        <Select
                          value={branchFilter}
                          onChange={(e) => setBranchFilter(e.target.value)}
                          displayEmpty
                          inputProps={{ "aria-label": "Branch" }}
                        >
                          <MenuItem value="All">All Branches</MenuItem>
                          {branches.map((b) => (
                            <MenuItem key={b} value={b}>
                              {b}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl size="small" className="w-full sm:w-48">
                        <Select
                          value={typeFilter}
                          onChange={(e) => setTypeFilter(e.target.value)}
                          displayEmpty
                          inputProps={{ "aria-label": "Problem type" }}
                        >
                          <MenuItem value="All">All Problem Types</MenuItem>
                          {problemTypes.map((t) => (
                            <MenuItem key={t} value={t}>
                              {problemTypeLabel(t)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <TextField
                        size="small"
                        placeholder="Search order #, customer, reporter..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="col-span-2 w-full sm:w-72"
                        inputProps={{ "aria-label": "Search order problems" }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </div>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="app-table">
                      <thead>
                        <tr>
                          <th className="!px-4">Reported</th>
                          <th className="!px-4">Branch</th>
                          <th className="!px-4">Order #</th>
                          <th className="!px-4">Order Status</th>
                          <th className="!px-4">Customer</th>
                          <th className="!px-4">Problem</th>
                          <th className="!px-4">Status</th>
                          <th className="!px-4">
                            <span className="sr-only">Open</span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {problems === null ? (
                          <tr>
                            <td colSpan={8} className="!py-12">
                              <div className="flex flex-col items-center gap-3">
                                <Spinner />
                                <p className="app-muted text-sm">
                                  Loading order problems...
                                </p>
                              </div>
                            </td>
                          </tr>
                        ) : filtered.length === 0 ? (
                          <tr className="hover:!bg-transparent">
                            <td colSpan={8} className="!p-0">
                              {problems.length === 0 ? (
                                <EmptyState
                                  compact
                                  icon={ReportProblemOutlinedIcon}
                                  title="No order problems have been reported yet."
                                />
                              ) : (
                                <EmptyState
                                  compact
                                  icon={FilterAltOffOutlinedIcon}
                                  title="Nothing matches the current filters."
                                  description="Try another status, branch, or problem type, or clear the search."
                                />
                              )}
                            </td>
                          </tr>
                        ) : (
                          filtered.map((i) => (
                            <tr
                              key={i._id}
                              onClick={() => selectProblem(i._id)}
                              className="cursor-pointer"
                            >
                              {/* Open problems get a rose edge so they stand
                                  out when scanning a mixed list. */}
                              <td
                                className={`whitespace-nowrap !px-4 ${
                                  i.status === "open"
                                    ? "shadow-[inset_3px_0_0_rgba(244,63,94,0.75)]"
                                    : ""
                                }`}
                              >
                                <div className="font-medium">
                                  {timeAgo(i.createdAt)}
                                </div>
                                <div className="app-muted text-xs">
                                  {reporterLabel(i)}
                                </div>
                              </td>
                              <td className="whitespace-nowrap !px-4">
                                {i.branch || "—"}
                              </td>
                              <td className="whitespace-nowrap !px-4 font-semibold tabular-nums">
                                {i.orderNumber || "—"}
                              </td>
                              <td className="whitespace-nowrap !px-4">
                                {i.orderNumber ? (
                                  <OrderStatusChip
                                    info={orderInfo[i.orderNumber]}
                                  />
                                ) : (
                                  <span className="app-muted">—</span>
                                )}
                              </td>
                              <td className="max-w-[220px] truncate !px-4">
                                {i.customer || "—"}
                              </td>
                              <td className="max-w-[280px] !px-4">
                                <ProblemChips problem={i} />
                              </td>
                              <td className="whitespace-nowrap !px-4">
                                <StatusChip status={i.status} />
                              </td>
                              <td className="whitespace-nowrap !px-4 text-right">
                                <span className="app-muted inline-flex items-center gap-2.5">
                                  {i.imageCount > 0 && (
                                    <span
                                      className="inline-flex items-center gap-1 text-xs tabular-nums"
                                      title={`${i.imageCount} photo${
                                        i.imageCount === 1 ? "" : "s"
                                      }`}
                                    >
                                      <PhotoLibraryIcon sx={{ fontSize: 15 }} />
                                      {i.imageCount}
                                    </span>
                                  )}
                                  {i.noteCount > 0 && (
                                    <span
                                      className="inline-flex items-center gap-1 text-xs tabular-nums"
                                      title={`${i.noteCount} note${
                                        i.noteCount === 1 ? "" : "s"
                                      }`}
                                    >
                                      <ChatBubbleOutlineIcon
                                        sx={{ fontSize: 15 }}
                                      />
                                      {i.noteCount}
                                    </span>
                                  )}
                                  <ChevronRightIcon fontSize="small" />
                                </span>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                  {problems !== null && filtered.length > 0 && (
                    <div className="app-muted border-t app-divider px-4 py-2.5 text-xs tabular-nums">
                      Showing {filtered.length} of {problems.length} order
                      problem{problems.length === 1 ? "" : "s"}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </PageShell>
    </ThemeProvider>
  );
};

export default OrderProblemsDashboard;
